import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, ExternalLink, CreditCard, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * Loaded on demand rather than from index.html: the app makes no third-party
 * request until someone actually chooses to pay.
 */
function loadCheckout() {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STATUS_TONE = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  TRIALING: "bg-blue-50 text-blue-700 border-blue-200",
  PAST_DUE: "bg-amber-50 text-amber-700 border-amber-200",
  PENDING: "bg-slate-100 text-slate-600 border-slate-200",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
  NONE: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Billing() {
  const { role, organization, organizations } = useAuth();
  const isOwner = role === "OWNER";
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [addons, setAddons] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/billing/config").then(({ data }) => {
      setCfg(data);
      setAddons(data.current?.addons || []);
    });
    api.get("/usage").then(({ data }) => setUsage(data));
    api.get("/billing/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!cfg) return <div className="text-sm text-slate-500">Loading billing…</div>;

  const sub = cfg.subscription || {};
  const currentPlanId = cfg.current?.plan_id;
  // A second Tally company has no plan of its own: the company it was added
  // from pays for both, so plans and payment are managed only over there.
  const billedElsewhere = !!sub.billed_elsewhere;
  const payer = (organizations || []).find((o) => o.id === sub.billing_organization_id);
  const payerName = payer?.tally_name || payer?.name || "your main company";

  const goToPayer = async () => {
    try {
      await api.post("/auth/switch-organization", { organization_id: payer.id });
      window.location.assign("/billing");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const priceOf = (plan) => (cycle === "yearly" ? plan.price_yearly : plan.price_monthly);
  const addonPrice = (a) => (cycle === "yearly" ? a.price_yearly : a.price_monthly);

  const subscribe = async (plan) => {
    if (!cfg.configured) {
      toast.error("Online payment is not configured on this deployment yet.");
      return;
    }
    setBusy(plan.id);
    try {
      const allowed = addons.filter((k) => (plan.available_addons || []).includes(k));
      const { data } = await api.post("/billing/subscribe", {
        plan_id: plan.id, billing_cycle: cycle, addons: allowed,
      });

      const ok = await loadCheckout();
      if (!ok) {
        // Razorpay also emails a hosted payment link, so this is recoverable.
        toast.error("Could not load the payment window.");
        if (data.short_url) window.open(data.short_url, "_blank", "noopener");
        setBusy(null);
        return;
      }

      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "SyncBooks",
        description: `${data.plan.name} — ${cycle}`,
        prefill: { email: organization?.email || "" },
        theme: { color: "#1d4ed8" },
        handler: () => {
          // Entitlement is granted by the webhook, not here — a browser
          // cannot be trusted to say it paid.
          toast.success("Payment received. Your plan updates within a few seconds.");
          setTimeout(load, 4000);
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.on("payment.failed", (r) =>
        toast.error(r?.error?.description || "The payment failed."));
      rzp.open();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(null);
  };

  const cancel = async () => {
    if (!window.confirm("Cancel at the end of the current billing cycle?")) return;
    try {
      await api.post("/billing/cancel", { at_cycle_end: true });
      toast.success("Subscription will end at the close of this cycle");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Billing</h1>
        <p className="mt-1 text-sm text-slate-500">Your plan, usage and payment history</p>
      </div>

      {billedElsewhere && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
          data-testid="billing-billed-elsewhere">
          <span>
            This company is covered by the plan on <strong>{payerName}</strong>. Plans,
            payments and invoices for all your companies are managed there.
          </span>
          {payer && (
            <Button variant="outline" onClick={goToPayer} data-testid="billing-go-to-payer"
              className="border-blue-300 bg-white text-blue-800 hover:bg-blue-100">
              Go to {payerName}
            </Button>
          )}
        </div>
      )}

      {!billedElsewhere && !cfg.configured && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800"
          data-testid="billing-not-configured">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Online payment is not configured on this deployment. Plans can still be assigned by
            your administrator. To enable self-service payment, set <code>RAZORPAY_KEY_ID</code>,{" "}
            <code>RAZORPAY_KEY_SECRET</code> and <code>RAZORPAY_WEBHOOK_SECRET</code> in
            backend/.env.
          </span>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="current-plan">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Current plan{billedElsewhere && ` · from ${payerName}`}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="font-heading text-2xl font-bold">{sub.plan_name || "No plan"}</span>
              <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold uppercase ${
                STATUS_TONE[sub.status] || STATUS_TONE.NONE}`}>
                {sub.status || "NONE"}
              </span>
            </div>
            {sub.total_monthly != null && (
              <div className="mt-1 text-sm text-slate-600">
                {inr(sub.plan_price_monthly)}
                {sub.addons_price_monthly > 0 && ` + ${inr(sub.addons_price_monthly)} add-ons`}
                {" = "}
                <strong className="font-mono">{inr(sub.total_monthly)}</strong> / month
              </div>
            )}
            {sub.current_period_end && (
              <div className="mt-0.5 text-[11px] text-slate-500">
                Renews {fmtDate(sub.current_period_end)}
              </div>
            )}
          </div>
          {isOwner && !billedElsewhere && cfg.current?.razorpay?.subscription_id && (
            <Button variant="outline" onClick={cancel} data-testid="billing-cancel"
              className="border-rose-200 text-rose-700 hover:bg-rose-50">
              Cancel subscription
            </Button>
          )}
        </div>

        {usage && (
          <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(usage.limits).map(([key, u]) => (
              <div key={key} data-testid={`usage-${key}`}>
                <div className="flex items-baseline justify-between text-[11px]">
                  <span className="uppercase tracking-wide text-slate-500">{u.label}</span>
                  <span className={`font-mono ${u.exceeded ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
                    {u.used}{u.limit == null ? "" : ` / ${u.limit}`}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${u.exceeded ? "bg-rose-500" : "bg-blue-500"}`}
                    style={{
                      width: u.limit == null ? "12%" : `${Math.min(100, (u.used / u.limit) * 100)}%`,
                      opacity: u.limit == null ? 0.35 : 1,
                    }}
                  />
                </div>
                {u.limit == null && (
                  <div className="mt-0.5 text-[10px] text-slate-400">Unlimited</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!billedElsewhere && (
      <>
      <div className="flex items-center gap-2">
        {["monthly", "yearly"].map((c) => (
          <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-150 ${
              cycle === c ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
            }`}>
            {c === "yearly" ? "Yearly (2 months free)" : "Monthly"}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {cfg.plans.map((p) => {
          const current = p.id === currentPlanId;
          return (
            <div key={p.id} data-testid={`plan-${p.code}`}
              className={`flex flex-col rounded-lg border bg-white p-5 ${
                current ? "border-blue-600 ring-1 ring-blue-600" : "border-slate-200"}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg font-bold">{p.name}</span>
                {current && (
                  <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-700">
                    <Check size={10} /> Current
                  </span>
                )}
              </div>
              <div className="mt-2">
                <span className="font-heading text-2xl font-extrabold tabular-nums">{inr(priceOf(p))}</span>
                <span className="text-xs text-slate-500">/{cycle === "yearly" ? "year" : "month"}</span>
              </div>
              <p className="mt-2 min-h-[32px] text-xs text-slate-600">{p.description}</p>
              <ul className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-[11px] text-slate-600">
                <li>{p.max_users == null ? "Unlimited" : p.max_users} team members</li>
                <li>{p.max_companies == null ? "Unlimited" : p.max_companies} Tally {p.max_companies === 1 ? "company" : "companies"}</li>
                <li>{p.max_vouchers_per_company == null ? "Unlimited" : p.max_vouchers_per_company.toLocaleString("en-IN")} vouchers / company</li>
                <li>{p.module_keys?.length} modules included</li>
              </ul>

              {!!(p.available_addons || []).length && (
                <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Optional add-ons</div>
                  {p.available_addons.map((key) => {
                    const a = cfg.addons.find((x) => x.key === key);
                    if (!a) return null;
                    return (
                      <label key={key} className="flex cursor-pointer items-center justify-between gap-2 text-[11px]"
                        data-testid={`addon-${p.code}-${key}`}>
                        <span className="flex items-center gap-1.5">
                          <input type="checkbox" className="accent-blue-600"
                            checked={addons.includes(key)}
                            onChange={() => setAddons((s) =>
                              s.includes(key) ? s.filter((k) => k !== key) : [...s, key])} />
                          {a.name}
                        </span>
                        <span className="font-mono text-amber-700">+{inr(addonPrice(a))}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 border-t border-slate-100 pt-3">
                {isOwner ? (
                  <Button
                    className="w-full gap-1.5 bg-blue-600 hover:bg-blue-700"
                    disabled={busy === p.id || priceOf(p) <= 0 || !cfg.configured}
                    onClick={() => subscribe(p)}
                    data-testid={`subscribe-${p.code}`}
                  >
                    {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                    {priceOf(p) <= 0 ? "Free plan" : current ? "Renew / change" : "Subscribe"}
                  </Button>
                ) : (
                  <div className="text-center text-[11px] text-slate-500">Only the Owner can change the plan</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!!invoices.length && (
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">Payment history</div>
          <div className="divide-y divide-slate-100" data-testid="billing-invoices">
            {invoices.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                <div>
                  <span className="font-mono text-xs">{i.id}</span>
                  <span className="ml-2 text-[11px] text-slate-500">
                    {i.paid_at ? fmtDate(new Date(i.paid_at * 1000).toISOString()) : i.status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono tabular-nums">{inr(i.amount)}</span>
                  {i.short_url && (
                    <a href={i.short_url} target="_blank" rel="noreferrer"
                      className="text-slate-400 hover:text-slate-700">
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
